import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, FileSpreadsheet, Loader2, UploadIcon } from "lucide-react";
import { useBulkImportClients } from "creso-core/hooks";
import type { BulkImportLeadRow, BulkImportRowResult } from "creso-core/api";
import { toast } from "sonner";
import { read as readXlsx, utils as xlsxUtils } from "xlsx";
import { handleError } from "@/utils/error";
import {
  Button,
  cn,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  V2Dialog,
  VisuallyHidden,
} from "web-design-system";
import {
  SuccessLottieAnimation,
  ErrorLottieAnimation,
} from "web-core/components";

type LeadHeaderKey = "mobile" | "name" | "pan" | "email";

const FIELD_CONFIG: Array<{
  key: LeadHeaderKey;
  label: string;
  required: boolean;
}> = [
  { key: "name", label: "Name", required: true },
  { key: "mobile", label: "Mobile", required: true },
  { key: "pan", label: "PAN (optional)", required: false },
  { key: "email", label: "Email (optional)", required: false },
];

function parseRow(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && nextChar === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

function padRowToLength(row: string[], length: number): string[] {
  const next = row.slice(0, length);
  while (next.length < length) {
    next.push("");
  }
  return next;
}

/** Radix Select requires every SelectItem `value` to be a non-empty string. */
function columnSelectValue(index: number) {
  return `__bulk_upload_col_${index}`;
}

const HEADER_KEYWORDS: Record<LeadHeaderKey, string[]> = {
  mobile: ["mobile", "phone", "whatsapp", "cell"],
  pan: ["pan"],
  email: ["mail"],
  name: ["name"],
};

function normalizeHeader(h: string): string {
  return h
    .toLowerCase()
    .trim()
    .replace(/[\s\-_./]/g, "");
}

function autoMapHeaders(headers: string[]): Record<LeadHeaderKey, string> {
  const mapped: Record<LeadHeaderKey, string> = {
    mobile: "",
    name: "",
    pan: "",
    email: "",
  };
  const usedIndices = new Set<number>();

  (Object.keys(HEADER_KEYWORDS) as LeadHeaderKey[]).forEach((key) => {
    const keywords = HEADER_KEYWORDS[key];
    const index = headers.findIndex(
      (h, i) =>
        !usedIndices.has(i) &&
        keywords.some((k) => normalizeHeader(h).includes(k))
    );
    if (index !== -1) {
      mapped[key] = columnSelectValue(index);
      usedIndices.add(index);
    }
  });

  return mapped;
}

const SAMPLE_CSV_CONTENT = [
  "Name,Mobile,PAN,Email",
  "Sample Investor One,9000000001,AAAPA0000A,investor1@example.com",
  "Sample Investor Two,9000000002,AAAPA0000B,investor2@example.com",
  "Sample Investor Three,9000000003,,",
].join("\r\n");

function downloadSampleFile() {
  const blob = new Blob([SAMPLE_CSV_CONTENT], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "sample-bulk-upload-leads.csv";
  anchor.click();
  URL.revokeObjectURL(url);
}

const ACCEPTED_EXTENSIONS = /\.(csv|xlsx|xls)$/i;
const EXCEL_EXTENSIONS = /\.(xlsx|xls)$/i;

function isAcceptedFile(file: File): boolean {
  return ACCEPTED_EXTENSIONS.test(file.name);
}

function isExcelFile(file: File): boolean {
  return EXCEL_EXTENSIONS.test(file.name);
}

function parseExcelToRows(buffer: ArrayBuffer): {
  headers: string[];
  dataRows: string[][];
  sheetCount: number;
} {
  const workbook = readXlsx(buffer, { type: "array" });
  const sheetCount = workbook.SheetNames.length;
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) {
    return { headers: [], dataRows: [], sheetCount };
  }
  const raw: string[][] = xlsxUtils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    blankrows: false,
    rawNumbers: false,
  });
  if (raw.length === 0) {
    return { headers: [], dataRows: [], sheetCount };
  }
  const headers = raw[0].map(String);
  const width = Math.max(headers.length, 1);
  const dataRows = raw.slice(1).map((r) => {
    const row = r.map(String);
    return padRowToLength(row, width);
  });
  return { headers, dataRows, sheetCount };
}

function parseBulkUploadColumnIndex(value: string): number | null {
  const match = /^__bulk_upload_col_(\d+)$/.exec(value);
  return match ? Number(match[1]) : null;
}

function buildBulkImportRows(
  dataRows: string[][],
  mapped: Record<LeadHeaderKey, string>
): BulkImportLeadRow[] {
  const mobileIdx = parseBulkUploadColumnIndex(mapped.mobile);
  const nameIdx = parseBulkUploadColumnIndex(mapped.name);
  if (mobileIdx === null || nameIdx === null) {
    return [];
  }
  const panIdx = mapped.pan ? parseBulkUploadColumnIndex(mapped.pan) : null;
  const emailIdx = mapped.email
    ? parseBulkUploadColumnIndex(mapped.email)
    : null;

  return dataRows.map((row) => {
    const mobile = (row[mobileIdx] ?? "").trim();
    const name = (row[nameIdx] ?? "").trim();
    const rowPayload: BulkImportLeadRow = { mobile, name };
    if (panIdx !== null) {
      const pan = (row[panIdx] ?? "").trim();
      if (pan) {
        rowPayload.pan = pan;
      }
    }
    if (emailIdx !== null) {
      const email = (row[emailIdx] ?? "").trim();
      if (email) {
        rowPayload.email = email;
      }
    }
    return rowPayload;
  });
}

function escapeCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildFailedRows(
  failedResults: BulkImportRowResult[],
  previewRows: string[][],
  headers: string[]
): string {
  const extraHeaders = ["import_status", "import_reason", "import_message"];
  const headerRow = [...headers, ...extraHeaders];
  const bodyLines = failedResults
    .filter((r) => r.status === "failed")
    .map((r) => {
      const data = previewRows[r.row] ?? [];
      const padded = padRowToLength(data, headers.length);
      const row = [...padded, r.status, r.reason ?? "", r.message ?? ""];
      return row.map((c) => escapeCell(String(c))).join(",");
    });
  return [headerRow.map(escapeCell).join(","), ...bodyLines].join("\r\n");
}

function downloadFailedImport(
  results: BulkImportRowResult[],
  previewRows: string[][],
  headers: string[]
) {
  const failed = results.filter((r) => r.status === "failed");
  if (failed.length === 0) {
    return;
  }
  const csv = buildFailedRows(failed, previewRows, headers);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `bulk-import-failed-rows-${Date.now()}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function BulkUploadLeadDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [contactCount, setContactCount] = useState(0);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [mappedHeaders, setMappedHeaders] = useState<
    Record<LeadHeaderKey, string>
  >({
    mobile: "",
    name: "",
    pan: "",
    email: "",
  });
  const [importOutcome, setImportOutcome] = useState<{
    summary: { total: number; created: number; failed: number };
    results: BulkImportRowResult[];
  } | null>(null);

  const { mutateAsync: runBulkImport, isPending: isBulkImportPending } =
    useBulkImportClients();

  const resetBulkUploadState = useCallback(() => {
    setUploadedFile(null);
    setHeaders([]);
    setContactCount(0);
    setPreviewRows([]);
    setMappedHeaders({ mobile: "", name: "", pan: "", email: "" });
    setImportOutcome(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  useEffect(() => {
    if (!open) {
      resetBulkUploadState();
    }
  }, [open, resetBulkUploadState]);

  const applyFile = async (file: File | null) => {
    if (!file) {
      setUploadedFile(null);
      setHeaders([]);
      setContactCount(0);
      setPreviewRows([]);
      setMappedHeaders({ mobile: "", name: "", pan: "", email: "" });
      setImportOutcome(null);
      return;
    }

    if (!isAcceptedFile(file)) {
      toast.error("Please upload a CSV or Excel file (.csv, .xlsx, .xls).");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setImportOutcome(null);
    setUploadedFile(file);
    try {
      let headers: string[];
      let paddedDataRows: string[][];

      if (isExcelFile(file)) {
        const buffer = await file.arrayBuffer();
        const parsed = parseExcelToRows(buffer);
        if (parsed.sheetCount > 1) {
          toast.error(
            "Excel file contains multiple sheets. Please upload a file with only 1 sheet."
          );
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          setUploadedFile(null);
          return;
        }
        headers = parsed.headers;
        paddedDataRows = parsed.dataRows;
      } else {
        const content = await file.text();
        const nonEmptyLines = content
          .split(/\r?\n/)
          .filter((line) => line.trim().length > 0);

        if (nonEmptyLines.length === 0) {
          setHeaders([]);
          setContactCount(0);
          setPreviewRows([]);
          setMappedHeaders({ mobile: "", name: "", pan: "", email: "" });
          setImportOutcome(null);
          return;
        }

        headers = parseRow(nonEmptyLines[0]);
        const dataLines = nonEmptyLines.slice(1);
        const width = Math.max(headers.length, 1);
        paddedDataRows = dataLines.map((line) =>
          padRowToLength(parseRow(line), width)
        );
      }

      if (headers.length === 0) {
        setHeaders([]);
        setContactCount(0);
        setPreviewRows([]);
        setMappedHeaders({ mobile: "", name: "", pan: "", email: "" });
        setImportOutcome(null);
        return;
      }

      setHeaders(headers);
      setContactCount(paddedDataRows.length);
      setPreviewRows(paddedDataRows);
      setMappedHeaders(autoMapHeaders(headers));
    } catch {
      setHeaders([]);
      setContactCount(0);
      setPreviewRows([]);
      setMappedHeaders({ mobile: "", name: "", pan: "", email: "" });
      setImportOutcome(null);
    }
  };

  const canSubmit = useMemo(() => {
    return Boolean(
      uploadedFile &&
      mappedHeaders.mobile &&
      mappedHeaders.name &&
      !importOutcome
    );
  }, [importOutcome, mappedHeaders.mobile, mappedHeaders.name, uploadedFile]);

  const handleAddLeads = useCallback(async () => {
    if (!canSubmit || previewRows.length === 0) {
      return;
    }
    const rows = buildBulkImportRows(previewRows, mappedHeaders);
    if (rows.length === 0) {
      toast.error("Could not build rows from the file and column mapping.");
      return;
    }
    try {
      const response = await runBulkImport(rows);
      setImportOutcome({
        summary: response.summary,
        results: response.results,
      });
    } catch (error) {
      handleError(error, "Failed to import leads");
    }
  }, [runBulkImport, canSubmit, mappedHeaders, previewRows]);

  const importFailedSummaryLine = useMemo(() => {
    if (!importOutcome || importOutcome.summary.failed === 0) {
      return "";
    }
    const { failed } = importOutcome.summary;

    return `${failed} ${failed === 1 ? "row" : "rows"} import failed`;
  }, [importOutcome]);

  const columnOptions = useMemo(
    () =>
      headers.map((header, index) => ({
        value: columnSelectValue(index),
        label: header.trim() ? header : `Column ${index + 1}`,
      })),
    [headers]
  );

  return (
    <V2Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[575px] max-w-[920px] flex-col gap-0 overflow-hidden p-0 rounded-3xl border-none">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Upload file</DialogTitle>
            <DialogDescription>
              Upload lead file and map headers
            </DialogDescription>
          </DialogHeader>
        </VisuallyHidden>

        <Input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            applyFile(file).catch(() => undefined);
          }}
        />

        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] overflow-hidden lg:grid-cols-[1.65fr_1fr] lg:grid-rows-1">
          <div className="flex h-full min-h-0 flex-col gap-5 overflow-hidden border-border p-7 lg:border-r">
            <span className="shrink-0 text-md font-semibold">Upload Leads</span>
            {uploadedFile ? (
              <div className="flex min-h-0 flex-1 flex-col gap-5">
                <div className="flex shrink-0 flex-row items-center justify-between gap-3">
                  <p className="text-sm text-foreground">
                    Preview of your upload, contains {contactCount}{" "}
                    {contactCount === 1 ? "row" : "rows"}
                  </p>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto shrink-0 p-0 text-sm font-semibold text-primary underline-offset-2 hover:underline"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                      fileInputRef.current?.click();
                    }}
                  >
                    Choose another
                  </Button>
                </div>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border">
                  <ScrollArea className="h-full min-h-0 min-w-0 flex-1">
                    <div className="max-w-full min-w-full !flex-none overflow-x-auto">
                      <table className="w-max min-w-full border-collapse text-sm [&_td]:align-top [&_th]:align-top">
                        <thead>
                          <tr className="border-b border-border bg-muted/50">
                            {headers.map((header, headerIndex) => (
                              <th
                                key={`header-${headerIndex}`}
                                className="border-r border-border px-3 py-2.5 text-left text-sm font-medium whitespace-nowrap last:border-r-0"
                              >
                                {header || "—"}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows.map((row, rowIndex) => (
                            <tr
                              key={rowIndex}
                              className="border-b border-border last:border-b-0"
                            >
                              {row.map((cell, cellIndex) => (
                                <td
                                  key={cellIndex}
                                  className="border-r border-border px-3 py-2 whitespace-nowrap last:border-r-0"
                                  title={cell}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </ScrollArea>
                </div>
              </div>
            ) : (
              <div
                className="flex min-h-0 flex-1 cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border text-center"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0] ?? null;
                  applyFile(file).catch(() => undefined);
                }}
              >
                <UploadIcon className="size-9 text-muted-foreground" />
                <p className="flex flex-col gap-2 text-sm leading-8">
                  <span>Drag & drop a file or</span>
                  <span className="text-blue-600 dark:text-blue-400">
                    Upload from computer
                  </span>
                </p>
              </div>
            )}
            <div className="flex flex-row gap-1.5 items-center">
              <Button
                type="button"
                variant="link"
                className="h-auto shrink-0 gap-1.5 p-0 text-sm text-foreground"
                onClick={downloadSampleFile}
              >
                <FileSpreadsheet className="size-4" aria-hidden />
                <span>Download sample file</span>
              </Button>
              <span className="text-sm text-muted-foreground font-normal">
                Accepted file format .XLS .CSV
              </span>
            </div>
          </div>

          <div className="flex min-h-0 flex-col gap-5 overflow-hidden bg-muted p-7">
            {importOutcome ? (
              <>
                <div className="flex flex-1 flex-col items-center justify-center gap-4">
                  {importOutcome.summary.created > 0 ? (
                    <SuccessLottieAnimation
                      loop={false}
                      height={100}
                      width={100}
                    />
                  ) : (
                    <ErrorLottieAnimation
                      loop={false}
                      height={100}
                      width={100}
                    />
                  )}
                  <p className="text-lg font-semibold">
                    Added {importOutcome.summary.created}{" "}
                    {importOutcome.summary.created === 1 ? "Lead" : "Leads"}
                  </p>
                </div>
                {importOutcome.summary.failed === 0 ? (
                  <Button
                    className="w-full shrink-0 font-semibold"
                    size="default"
                    type="button"
                    onClick={() => onOpenChange(false)}
                  >
                    Done
                  </Button>
                ) : (
                  <>
                    <div className="flex shrink-0 flex-row items-center gap-3 rounded-xl bg-secondary px-4 py-3 border">
                      <div className="min-w-0 flex-1 flex flex-col gap-1">
                        <p className="text-sm text-destructive">
                          {importFailedSummaryLine}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Download to update details.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-foreground bg-background"
                        title="Download failed rows as CSV"
                        onClick={() => {
                          downloadFailedImport(
                            importOutcome.results,
                            previewRows,
                            headers
                          );
                        }}
                      >
                        <Download className="size-5" />
                      </Button>
                    </div>
                    <Button
                      className={cn(
                        "w-full shrink-0 gap-2 font-semibold [&_svg]:shrink-0"
                      )}
                      size="default"
                      type="button"
                      onClick={() => {
                        resetBulkUploadState();
                      }}
                    >
                      Try Again
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <span className="shrink-0 text-md font-semibold">
                  Select headers from your file
                </span>
                <div className="flex flex-col gap-5 flex-1 overflow-auto">
                  {FIELD_CONFIG.map((field) => {
                    const fieldControlId = `bulk-upload-lead-map-${field.key}`;
                    return (
                      <div key={field.key} className="flex flex-col gap-2">
                        <label className="text-sm" htmlFor={fieldControlId}>
                          {field.label}
                          {field.required ? (
                            <span className="text-destructive" aria-hidden>
                              {" "}
                              *
                            </span>
                          ) : null}
                        </label>
                        <Select
                          key={`${field.key}-${uploadedFile?.name ?? ""}-${uploadedFile?.lastModified ?? 0}-${uploadedFile?.size ?? 0}`}
                          value={
                            mappedHeaders[field.key]
                              ? mappedHeaders[field.key]
                              : undefined
                          }
                          onValueChange={(value) => {
                            setMappedHeaders((prev) => ({
                              ...prev,
                              [field.key]: value,
                            }));
                          }}
                          disabled={
                            headers.length === 0 || importOutcome !== null
                          }
                        >
                          <SelectTrigger
                            id={fieldControlId}
                            aria-required={field.required}
                            className="h-11 bg-background focus:ring-0 focus:ring-offset-0 focus-visible:ring-0"
                          >
                            <SelectValue placeholder="Select header" />
                          </SelectTrigger>
                          <SelectContent>
                            {columnOptions.map(({ value, label }) => (
                              <SelectItem
                                key={`${field.key}-${value}`}
                                value={value}
                              >
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
                <Button
                  className={cn(
                    "w-full shrink-0 gap-2 font-semibold [&_svg]:shrink-0",
                    isBulkImportPending && "cursor-wait [&_svg]:size-5"
                  )}
                  size="default"
                  type="button"
                  onClick={() => {
                    handleAddLeads();
                  }}
                  disabled={
                    importOutcome ? false : !canSubmit || isBulkImportPending
                  }
                >
                  {isBulkImportPending ? (
                    <>
                      <Loader2
                        className="animate-spin text-primary-foreground"
                        aria-hidden
                      />
                      Processing...
                    </>
                  ) : (
                    `Add Leads${contactCount ? ` (${contactCount})` : ""}`
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </V2Dialog>
  );
}
